/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import useRoutes from "../../routes";
import axios from "axios";
import moment from "moment";
import Card from "components/card";
import { Button } from "@chakra-ui/react";
import { useIdleTimer } from "react-idle-timer";
import NotFound from "../../views/public/NotFound";
import ImpersonationBanner from "components/ImpersonationBanner";

const defaultSessionExpireCountdownInSeconds = 60;
const defaultIdleTimeoutInMinute = 15;
const theme = process.env.REACT_APP_THEME || "default";

export default function Layout(props: {
  [x: string]: any;
  userRole: any;
  userPermissions: any;
}) {
  const { userRole, userPermissions } = props;
  const { routes } = useRoutes(userPermissions);
  const currentRoleRoutes = userRole ? routes[userRole.toLowerCase()] || [] : [];
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [currentRoute, setCurrentRoute] = useState("Dashboard");
  const [isIdleModalOpen, setIsIdleModalOpen] = useState(false);
  const [isSessionConflict, setIsSessionConflict] = useState(false);
  const frozenRoutesRef = useRef<RoutesType[]>(currentRoleRoutes);
  if (!isSessionConflict) {
    frozenRoutesRef.current = currentRoleRoutes;
  }
  const roleRoutes = frozenRoutesRef.current;
  const [countdown, setCountdown] = useState(
    defaultSessionExpireCountdownInSeconds
  );
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionSnapshot = useRef({
    userRole: localStorage.getItem("userRole"),
    isImpersonating: localStorage.getItem("isImpersonating"),
  });

  useEffect(() => {
    window.addEventListener("resize", () =>
      window.innerWidth < 1200 ? setOpen(false) : setOpen(true)
    );
  }, []);

  useEffect(() => {
    const updateFavicon = (
      newFavicon: string,
      newFavicon32: string,
      newFavicon16: string,
      newFavicon180: string
    ) => {
      const icon = document.querySelector(
        "link[data-favicon-ico]"
      ) as HTMLLinkElement;
      const icon32 = document.querySelector(
        'link[rel="icon"][sizes="32x32"]'
      ) as HTMLLinkElement;
      const icon16 = document.querySelector(
        'link[rel="icon"][sizes="16x16"]'
      ) as HTMLLinkElement;
      const icon180 = document.querySelector(
        'link[rel="apple-touch-icon"][sizes="180x180"]'
      ) as HTMLLinkElement;

      if (icon) {
        icon.href = "";
      }

      if (icon32) {
        icon32.href = "";
      }

      if (icon16) {
        icon16.href = "";
      }

      if (icon180) {
        icon180.href = "";
      }

      icon.href = `${newFavicon}?t=${new Date().getTime()}`;
      icon32.href = `${newFavicon32}?t=${new Date().getTime()}`;
      icon16.href = `${newFavicon16}?t=${new Date().getTime()}`;
      icon180.href = `${newFavicon180}?t=${new Date().getTime()}`;
    };

    updateFavicon(
      `/themes/${theme}/favicon.ico`,
      `/themes/${theme}/favicon-32x32.png`,
      `/themes/${theme}/favicon-16x16.png`,
      `/themes/${theme}/favicon-180x180.png`
    );

    const manifest = document.querySelector("link[rel='manifest']");
    if (manifest)
      manifest.setAttribute("href", `/themes/${theme}/site.webmanifest`);
  }, []);

  useEffect(() => {
    if (isSessionConflict) return;
    if (roleRoutes.length > 0) {
      const pathnameSegments = location.pathname.split("/");
      const pathSegment = pathnameSegments[2];
      if (
        roleRoutes.filter((x: any) => x.path.includes(pathSegment)).length !== 0
      ) {
        getActiveRoute(roleRoutes);
      } else {
        navigate(`/${userRole.toLowerCase()}/dashboard`);
      }
    }
  }, [location.pathname, roleRoutes, isSessionConflict]);

  const getActiveRoute = (
    routes: RoutesType[] | undefined
  ): string | boolean => {
    if (!routes || routes.length === 0) {
      return "Dashboard";
    }

    let activeRoute = "Dashboard";
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(
          routes[i].layout + "/" + routes[i].path
        ) !== -1
      ) {
        setCurrentRoute(routes[i].name);
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (
    routes: RoutesType[] | undefined
  ): string | boolean => {
    if (!routes || routes.length === 0) {
      return false;
    }

    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
      ) {
        return routes[i].secondary;
      }
    }

    return false;
  };

  const getRoutes = (routes: RoutesType[] | undefined): any => {
    if (!routes || routes.length === 0) {
      return null;
    }

    return routes.map((prop, key) => {
      return (
        <Route
          path={`/${prop.layout}/${prop.path}`}
          element={prop.component}
          key={key}
        />
      );
    });
  };

  const cleanupBeforeSignedOut = () => {
    setCountdown(defaultSessionExpireCountdownInSeconds);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const navigateToSignin = () => {
    cleanupBeforeSignedOut();
    navigate(`/auth/sign-in`);
  };

  const navigateToSigninCauseOfIdle = () => {
    cleanupBeforeSignedOut();
    const link = document.createElement("a");
    link.href = "/auth/sign-in?idle=true";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const startCountdown = () => {
    const targetTime =
      Date.now() + defaultSessionExpireCountdownInSeconds * 1000;
    let remainingTime = defaultSessionExpireCountdownInSeconds;

    const id = setInterval(() => {
      remainingTime = Math.max(0, Math.round((targetTime - Date.now()) / 1000));

      if (remainingTime <= 0) {
        clearInterval(id);
        setCountdown(0);
        navigateToSigninCauseOfIdle();
      }

      setCountdown(remainingTime);
    }, 1000);

    localStorage.setItem("startCountdownId", String(id));
    countdownIntervalRef.current = id;
  };

  const handleOnIdle = () => {
    setIsIdleModalOpen(true);
    setCountdown(60);
  };

  const handleOnActive = () => {
    console.log("User noticed");
  };

  const { reset: resetIdleTimer } = useIdleTimer({
    timeout: 1000 * 60 * defaultIdleTimeoutInMinute,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    debounce: 500,
    crossTab: true,
    syncTimers: 200,
  });

  useEffect(() => {
    if (isIdleModalOpen) {
      startCountdown();
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      localStorage.removeItem("startCountdownId");
    };
  }, [isIdleModalOpen]); // eslint-disable-line

  const checkTokenExpires = () => {
    const apiTokenExpiresFromStorage = localStorage.getItem("apiTokenExpires");
    const apiTokenExpires = moment(apiTokenExpiresFromStorage);
    const currentTime = moment();
    const differenceInSeconds = apiTokenExpires.diff(currentTime, "seconds");
    if (differenceInSeconds <= 3600) {
      return true;
    } else {
      return false;
    }
  };

  const extendTokenExpires = async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/user/request`,
      {
        fn: "extend-auth",
      }
    );

    let { token, expires } = response.data;

    if (token != null) {
      localStorage.setItem("apiToken", token);
      localStorage.setItem("apiTokenExpires", expires);
    } else {
      navigateToSignin();
    }
  };

  const handleStaySignedIn = () => {
    setIsIdleModalOpen(false);
    clearInterval(countdownIntervalRef.current!);
    countdownIntervalRef.current = null;
    resetIdleTimer();

    // Signal other tabs to dismiss their idle modal
    localStorage.setItem("idleDismissed", Date.now().toString());

    if (checkTokenExpires()) {
      extendTokenExpires();
    }
  };

  // Listen for idle modal dismiss from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "idleDismissed" && e.newValue) {
        setIsIdleModalOpen(false);
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      }

      // Cross-tab session conflict detection (impersonation / role change)
      if (e.key === "userRole" || e.key === "isImpersonating") {
        const snap = sessionSnapshot.current;
        const changed =
          localStorage.getItem("userRole") !== snap.userRole ||
          localStorage.getItem("isImpersonating") !== snap.isImpersonating;
        if (changed) {
          setIsSessionConflict(true);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  document.documentElement.dir = "ltr";

  return (
    routes && (
      <>
        {isSessionConflict &&
          createPortal(
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10001,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "1rem",
              }}
            >
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                }}
              />
              <Card extra="relative z-[10002] px-[30px] pt-[35px] pb-[40px] w-full max-w-[520px] flex flex-col">
                <h1 className="mb-[20px] text-2xl font-bold text-navy-700">
                  Session Changed
                </h1>
                <div className="mb-[20px] text-navy-700">
                  Your session has changed. Please refresh to continue.
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => window.location.reload()}
                    className="rounded-xl bg-primary-500 px-3 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-400 dark:text-white dark:hover:bg-primary-300 dark:active:bg-primary-200"
                  >
                    Refresh
                  </Button>
                </div>
              </Card>
            </div>,
            document.body
          )}

        {isIdleModalOpen &&
          createPortal(
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "1rem",
              }}
            >
              <div
                id="extend-modal-overlay"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                }}
              />
              <Card extra="relative z-[10000] px-[30px] pt-[35px] pb-[40px] w-full max-w-[720px] flex flex-col">
                <h1 className="mb-[20px] text-2xl font-bold text-navy-700">
                  Are you still there?
                </h1>
                <div className="mb-[20px] text-navy-700">
                  For security reasons, you'll be signed out in{" "}
                  <strong>{countdown >= 0 ? countdown : "0"} seconds</strong>{" "}
                  due to inactivity. Click OK to stay signed in.
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => handleStaySignedIn()}
                    className="rounded-xl bg-primary-500 px-3 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-400 dark:text-white dark:hover:bg-primary-300 dark:active:bg-primary-200"
                  >
                    OK
                  </Button>
                </div>
              </Card>
            </div>,
            document.body
          )}

        {!isSessionConflict && (
          <div className="flex h-full w-full">
            <Sidebar
              open={open}
              onClose={() => setOpen(false)}
              userRole={userRole}
              userPermissions={userPermissions}
            />
            <div className="h-full w-full bg-lightPrimary xl:pl-80">
              <main className="h-full flex-none transition-all ">
                <div className="h-full">
                  <Navbar
                    isIdle={isIdleModalOpen}
                    onOpenSidenav={() => setOpen(!open)}
                    brandText={currentRoute}
                    secondary={getActiveNavbar(roleRoutes)}
                    userPermissions={userPermissions}
                  />
                  <div className="pt-5s mx-8 my-6 mb-auto h-full min-h-[84vh]">
                    <ImpersonationBanner />
                    <Routes>
                      {getRoutes(roleRoutes)}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </div>
                </div>
              </main>
            </div>
          </div>
        )}
      </>
    )
  );
}
